import { Component, OnInit, Input } from '@angular/core';
import { CacheService } from 'src/app/services/cache.service';
import { ScreenSizeService } from 'src/app/services/screen-size.service';
import { DIMBO_CONSTANT } from 'src/app/utils/constant';

// Vehicle info component.
@Component({
  selector: 'app-vehicle-info',
  templateUrl: './vehicle-info.component.html',
  styleUrls: ['./vehicle-info.component.scss']
})
export class VehicleInfoComponent implements OnInit {
  @Input() vehicleInfo: any;
  @Input() loading: boolean = false;
  @Input() isFromSearch: boolean = false; // Add this new input
  screenWidth: number = 0;

  vehicleBrand: string = '';
  vehicleModel: string = '';
  vin: string = '';
  vehicleRegistrationDate: string = '';
  vehicleWarrantyStartDate: string = '';
  vehicleLogoPath: string = '';
  locale: string = '';

  showFallback: boolean = false;

  constructor(
    private screenSizeService: ScreenSizeService,
    private cacheService: CacheService
  ) { }

  ngOnInit(): void {
    this.screenSizeService.screenWidth$.subscribe((width) => {
      this.screenWidth = width;
    });

    // On initial load, always use cache for vehicle info
    this.setVehicleInfoFromCache();
  }

 
ngOnChanges() {
  if (this.vehicleInfo?.shouldUpdateVehicleInfo) {
    if (this.vehicleInfo?.showFallback) {
      // Explicitly show fallback when requested
      this.clearVehicleInfo();
      this.showFallback = true;
    } else if (this.vehicleInfo && Object.keys(this.vehicleInfo).length > 0) {
      // Normal case with vehicle info
      this.setVehicleInfoFromInput(this.vehicleInfo);
      this.showFallback = false;
    } else {
      // Default to cached info
      this.setVehicleInfoFromCache();
      this.showFallback = false;
    }
  }
}

  /** Clear all vehicle info fields and set default placeholder logo. */
  private clearVehicleInfo() {
    this.vehicleBrand = '';
    this.vehicleModel = '';
    this.vin = '';
    this.vehicleRegistrationDate = '';
    this.vehicleWarrantyStartDate = '';
    this.vehicleLogoPath = 'assets/images/not-available-image.svg';
  }

  /**
   * Sets component vehicle fields from the input (locale, vinBrand, model, vin, warrantyStartDate).
   * Missing values default to ''. Resolves full brand name and logo path via helpers.
   */
  setVehicleInfoFromInput(info: any) {
    this.locale = info.locale || '';
    this.vehicleBrand = this.getFullBrandName(info.vinBrand, this.locale);
    this.vehicleModel = info.model || '';
    this.vin = info.vin || '';
    this.vehicleWarrantyStartDate = info.warrantyStartDate || '';
    this.vehicleLogoPath = `assets/images/${this.getLogoFileName(info.vinBrand, this.locale)}.svg`;
  }

  /**
   * Loads vehicle display fields from cached JWT values.
   * Missing cache entries become empty strings. Always clears showFallback.
   * Mutates public properties used by the template.
   */
  setVehicleInfoFromCache() {
    this.locale = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_LOCALE) || '';
    const brandName = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_VIN_BRAND) || '';
    this.vehicleBrand = this.getFullBrandName(brandName, this.locale);
    this.vehicleModel = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_MODEL) || '';
    this.vin = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_VIN) || '';
    this.vehicleRegistrationDate = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_FIRST_REGISTRATION_DATE) || '';
    this.vehicleWarrantyStartDate = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_WARRANTY_START_DATE) || '';
    this.vehicleLogoPath = `assets/images/${this.getLogoFileName(brandName, this.locale)}.svg`;
    this.showFallback = false;
  }

  /** Map brand short code to display name. OV => Vauxhall in en_GB else Opel. Unmapped returns code. */
  getFullBrandName(shortCode: string, locale: string): string {
    switch (shortCode) {
      case 'AP': return 'Peugeot';
      case 'AC': return 'Citroen';
      case 'DS': return 'DS';
      case 'OV': return locale === 'en_GB' ? 'Vauxhall' : 'Opel';
      default: return shortCode;
    }
  }

  /**
   * Map shortCode to logo file name.
   * OV => VX for en_GB, otherwise returns original code.
   */
  private getLogoFileName(shortCode: string, locale: string): string {
    if (shortCode === 'OV') {
      return locale === 'en_GB' ? 'VX' : 'OV';
    }
    return shortCode;
  }

  /**
   * Fallback for brand logo load errors.
   * Replaces failed img src with generic placeholder.
   * <img (error)="onLogoError($event)">
   */
  onLogoError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = 'assets/images/not-available-brand.svg';
  }
}
